import { DestroyRef, Injectable, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { httpResource } from '@angular/common/http';
import { UserService } from '../../Modules/Users/Services/user.service';
import { OrganizationService } from '../../Modules/Organizations/Services/organization.service';
import { ImpersonationService } from '../../Modules/Users/Services/impersonation.service';
import { ResourceStateService } from '../../Modules/Shared/Services/resource-state.service';
import { ErrorHandlerService } from '../../Modules/Shared/Services/error-handler.service';
import { ExternalLinkService } from '../../Modules/Shared/Services/external-link.service';
import { MsgService } from '../../Modules/Shared/Services/msg.service';
import { OperatorFacade } from '../operator.facade';
import { DEFAULT_PAGE_SIZE, FIRST_PAGE } from '../../Consts/backoffice.const';
import { SystemRole } from '../../Enums/system-role.enum';
import { MetricState } from '../../Modules/Shared/Types/metric-state.type';
import { UserFilters } from '../../Modules/Users/Interfaces/user-filters.interface';
import { OrganizationDetail } from '../../Models/backoffice/organization-detail.model';
import { TenantUser } from '../../Models/backoffice/tenant-user.model';

import { LaravelDefaultResponse } from '../../Interfaces/api/shared/response.interface';
import { OrganizationShowResponse } from '../../Interfaces/api/backoffice/organization.interface';
import { TenantUserListResponse } from '../../Interfaces/api/backoffice/user.interface';

@Injectable()
export class UsersFacade {
  private readonly _userService = inject(UserService);
  private readonly _organizationService = inject(OrganizationService);
  private readonly _impersonationService = inject(ImpersonationService);
  private readonly _resourceState = inject(ResourceStateService);
  private readonly _errorHandler = inject(ErrorHandlerService);
  private readonly _externalLink = inject(ExternalLinkService);
  private readonly _msgService = inject(MsgService);
  private readonly _operatorFacade = inject(OperatorFacade);
  private readonly _destroyRef = inject(DestroyRef);

  private readonly _filters = signal<UserFilters>({
    search: '',
    organization_id: '',
    app_status: '',
  });

  private readonly _page = signal<number>(FIRST_PAGE);
  private readonly _pageSize = signal<number>(DEFAULT_PAGE_SIZE);
  private readonly _isMutating = signal<boolean>(false);

  private readonly _users = httpResource<LaravelDefaultResponse<TenantUserListResponse>>(() =>
    this._userService.listRequest(this._filters(), this._page(), this._pageSize()),
  );

  private readonly _organization = httpResource<LaravelDefaultResponse<OrganizationShowResponse>>(
    () => this._organizationRequest(),
  );

  readonly filters = computed<UserFilters>(() => this._filters());

  readonly page = computed<number>(() => this._page());

  readonly pageSize = computed<number>(() => this._pageSize());

  readonly isMutating = computed<boolean>(() => this._isMutating());

  readonly users = computed<TenantUser[]>(
    () => this._userService.toPagedResult(this._resourceState.valueOf(this._users)).items,
  );

  readonly total = computed<number>(
    () => this._userService.toPagedResult(this._resourceState.valueOf(this._users)).total,
  );

  readonly state = computed<MetricState>(() =>
    this._resourceState.metricState(this._users.status()),
  );

  readonly isEmpty = computed<boolean>(() => this.state() === 'ready' && this.users().length === 0);

  readonly organizationId = computed<string>(() => this._filters().organization_id);

  readonly organization = computed<OrganizationDetail | null>(() =>
    this._organizationService.toDetail(this._resourceState.valueOf(this._organization)),
  );

  readonly rootIsMissing = computed<boolean>(() => this.organization()?.rootIsMissing ?? false);

  readonly canChangeRoot = computed<boolean>(
    () => this._operatorFacade.atLeast(SystemRole.BACKOFFICE_ADMIN) && this.organizationId() !== '',
  );

  readonly canImpersonate = computed<boolean>(() =>
    this._operatorFacade.atLeast(SystemRole.BACKOFFICE_ADMIN),
  );

  readonly hasEligibleRootCandidate = computed<boolean>(() =>
    this.users().some((user) => user.ownsCoreSeat),
  );

  applyFilters(filters: UserFilters): void {
    this._filters.set(filters);
    this._page.set(FIRST_PAGE);
  }

  setOrganization(organizationId: string): void {
    this._filters.update((filters) =>
      filters.organization_id === organizationId
        ? filters
        : { ...filters, organization_id: organizationId },
    );

    this._page.set(FIRST_PAGE);
  }

  goToPage(page: number): void {
    this._page.set(Math.max(FIRST_PAGE, page));
  }

  changePageSize(pageSize: number): void {
    this._pageSize.set(pageSize);
    this._page.set(FIRST_PAGE);
  }

  reload(): void {
    this._users.reload();
    this._organization.reload();
  }

  changeRoot(userId: string, override: boolean): void {
    const organizationId = this.organizationId();

    if (!organizationId) {
      return;
    }

    this._isMutating.set(true);

    this._organizationService
      .changeRoot(organizationId, userId, override)
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe({
        next: () => this._afterRootChange(),
        error: (error) => this._failMutation(error),
      });
  }

  impersonate(userId: string, reason: string | null): void {
    this._isMutating.set(true);

    this._impersonationService
      .issue(userId, reason)
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe({
        next: (response) => this._afterImpersonation(response.callback_url),
        error: (error) => this._failMutation(error),
      });
  }

  private _organizationRequest() {
    const organizationId = this._filters().organization_id;

    return organizationId ? this._organizationService.showRequest(organizationId) : undefined;
  }

  private _afterRootChange(): void {
    this._isMutating.set(false);
    this._msgService.showSnackBar('backoffice.users.root.changed', { type: 'success' });
    this.reload();
  }

  private _afterImpersonation(callbackUrl: string): void {
    this._isMutating.set(false);
    this._msgService.showSnackBar('backoffice.users.impersonation.issued', { type: 'success' });
    this._externalLink.openInNewTab(callbackUrl);
  }

  private _failMutation(error: unknown): void {
    this._isMutating.set(false);
    this._msgService.showSnackBar(this._errorHandler.messageFor(error), { type: 'error' });
  }
}
